<script setup lang="ts">
  import X from "@/components/icons/X.vue";

  type Props = {
    open: boolean;
    title?: string;
    subtitle?: string;
  };

  const props = defineProps<Props>();
  const emit = defineEmits<{ "update:open": [value: boolean] }>();

  function close() {
    emit("update:open", false);
  }
</script>

<template>
  <Teleport to="body">
    <Transition :name="$style.modal" appear>
      <div v-if="props.open" :class="$style.root" role="dialog" aria-modal="true">
        <!-- Tapping the backdrop closes the modal (cancels the action). -->
        <div :class="$style.backdrop" @click="close" />
        <div :class="$style.panel">
          <button type="button" :class="$style.close" aria-label="Закрыть" @click="close">
            <X :size="18" />
          </button>
          <div v-if="props.title || props.subtitle" :class="$style.header">
            <span v-if="props.title" :class="$style.title">{{ props.title }}</span>
            <span v-if="props.subtitle" :class="$style.subtitle">{{ props.subtitle }}</span>
          </div>
          <div :class="$style.content">
            <slot />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" module>
  .root {
    position: fixed;
    inset: 0;
    z-index: 1100;

    display: flex;
    align-items: center;
    justify-content: center;

    /* padding: 16px; */
  }

  .backdrop {
    position: absolute;
    inset: 0;

    background: rgb(0 0 0 / 0.5);
  }

  .panel {
    position: relative;
    width: 100%;
    max-width: 42rem;
    max-height: 90vh;

    display: flex;
    align-items: center;
    flex-direction: column;

    padding: 24px 20px 20px;

    background-color: var(--ksd-bg-color);
    color: var(--foreground);

    border: 1px solid var(--border-subtle);
    /* border-radius: var(--radius); */

    overflow-y: auto;
  }

  .close {
    position: absolute;
    top: 12px;
    right: 12px;

    display: flex;
    align-items: center;
    justify-content: center;

    padding: 4px;

    background: transparent;
    border: none;
    border-radius: var(--radius);
    color: var(--muted-foreground);
    cursor: pointer;

    &:hover {
      color: var(--foreground);
      background-color: var(--accent);
    }
  }

  .header {
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 6px;
  }

  .title {
    font-weight: 600;
    font-size: 1.125rem;
    line-height: 1.5rem;
    color: var(--foreground);
  }

  .subtitle {
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--muted-foreground);
  }

  .content {
    width: 100%;
    margin-top: 20px;
  }

  .modal:global(-enter-active),
  .modal:global(-leave-active) {
    transition: opacity 0.2s ease;

    .panel {
      transition: transform 0.2s ease;
    }
  }

  .modal:global(-enter-from),
  .modal:global(-leave-to) {
    opacity: 0;

    .panel {
      transform: scale(0.96);
    }
  }
</style>
